// React i hooks
import { useCallback, useEffect, useState } from "react";

// React-router
import { useNavigate, useParams } from "react-router-dom";

// API servisi
import { getAnnualTaxAPI } from "services/api/calculations/annualtax/api";

// Tipovi i konstante
import { AnnualTaxDetailFormValues } from "services/api/calculations/annualtax/types";
import { initialAnnualTaxDetailForm } from "services/api/calculations/annualtax/consts";
import { Errors } from "services/forms/validation/types";
import { AnnualTaxDetailPageCmp, AnnualTaxDetailPageProps } from "./types";
import { BreadcrumbItem } from "ui/components/Breadcrumb/types";
import { CALCULATION_ANNUAL_TAX_DETAIL_PAGE_TITLE } from "ui/components/PageTitle/consts";
import { SIDEBAR_ITEM_CALCULATION_ANNUAL_TAX } from "app/components/Sidebar/consts";

// UI komponente
import { Modal } from "ui/components/Modal";
import ModalBody from "ui/components/Modal/ModalBody";
import ModalHeader from "ui/components/Modal/ModalHeader";
import { PageHeader } from "ui/components/PageHeader";
import { PageTitle } from "ui/components/PageTitle";
import { Breadcrumb } from "ui/components/Breadcrumb";
import { Button } from "ui/components/Button/Button";

// Lokalne komponente
import AnnualTaxDetailCard from "./components/AnnualTaxDetailCard/AnnualTaxDetailCard";
import AnnualTaxDeleteCard from "./components/AnnualTaxDeleteCard/AnnualTaxDeleteCard";

// Helperi
import { transformRoute, useTranslate } from "helpers";
import { Routes } from "ui/modules/calculation/navigation";

const AnnualTaxDetailPage: AnnualTaxDetailPageCmp = (
  props: AnnualTaxDetailPageProps
) => {
  const { annualTaxId } = useParams();

  const { t } = useTranslate();
  const [errors, setErrors] = useState<Errors>({});
  const [isLoading, setIsLoading] = useState<boolean>(false);

  const [showDeleteModal, setShowDeleteModal] = useState<boolean>(false);

  const [formValues, setFormValues] = useState<AnnualTaxDetailFormValues>(
    initialAnnualTaxDetailForm
  );

  const navigate = useNavigate();

  const title = CALCULATION_ANNUAL_TAX_DETAIL_PAGE_TITLE;

  useEffect(() => {
    if (annualTaxId) fetchAnnualTax(annualTaxId);
  }, [annualTaxId]);

  const fetchAnnualTax = async (id: string) => {
    if (id === undefined) {
      setFormValues(initialAnnualTaxDetailForm);
      return;
    }

    setIsLoading(true);

    let result;
    try {
      result = await getAnnualTaxAPI(id);
    } catch {
      setIsLoading(false);
      return;
    }

    setFormValues(result);
    setIsLoading(false);

    setErrors({});
  };

  const breadcrumbs: BreadcrumbItem[] = [
    {
      text: SIDEBAR_ITEM_CALCULATION_ANNUAL_TAX.text,
      route: SIDEBAR_ITEM_CALCULATION_ANNUAL_TAX.route,
    },
  ];

  const handleDeleteSuccess = useCallback(() => {
    navigate(
      transformRoute(Routes.CalculationAnnualTaxSearchPageRouter, {
        annualTaxId,
      })
    );
  }, []);

  if (!annualTaxId) return <></>;

  return (
    <>
      <div className="">
        <div className="bg-white border-b border-slate-200 rounded-t-md">
          <PageHeader>
            <PageTitle
              title={`${title.text}`}
              icon={title.icon}
              iconColor={title.iconColor}
            />
            <div className="flex justify-between">
              <Breadcrumb items={breadcrumbs} />
              <div className="flex gap-2">
                <Button
                  size="large"
                  variant="remove"
                  children={t("admin.calculation.setting.annualtax.delete")}
                  onClick={() => setShowDeleteModal(true)}
                  startIcon="/media/icons/trash.svg"
                />
              </div>
            </div>
          </PageHeader>
        </div>
        <div className="px-4 py-6  lg:px-4">
          <AnnualTaxDetailCard annualTaxId={annualTaxId} />
        </div>
      </div>
      {showDeleteModal && (
        <Modal show={showDeleteModal} onHide={() => setShowDeleteModal(false)}>
          <ModalHeader
            title={t("admin.calculation.setting.annualtax.delete.title")}
            onHide={() => setShowDeleteModal(false)}
          />
          <ModalBody>
            <AnnualTaxDeleteCard
              annualTaxId={annualTaxId}
              onSuccess={handleDeleteSuccess}
            />
          </ModalBody>
        </Modal>
      )}
    </>
  );
};

AnnualTaxDetailPage.route = Routes.CalculationAnnualTaxDetailPageRouter;

export default AnnualTaxDetailPage;
